import React, { useContext, useEffect, useState } from 'react'
import { DContext } from '../../../context/Datacontext'
import { fetchStudentProfile } from '../../../services/user/fetchStudentProfile'
import { updateStudentProfile, createProfileFormData } from '../../../services/user/updateStudentProfileService'
import { studentProfileValidation } from '../../../schemas/studentProfileValidation'
import {
    FiUpload,
    FiSave,
    FiEdit2,
    FiTrash2,
    FiPlus,
    FiX,
    FiAlertCircle,
    FiCheckCircle,
    FiCamera,
    FiUser,
    FiBriefcase,
    FiBook,
    FiLink,
    FiLock,
    FiEye,
    FiEyeOff,
} from 'react-icons/fi'

function StudentProfile() {
    const { BeURL , showAlert } = useContext(DContext)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [studentCode, setStudentCode] = useState('')
    const [profileImage, setProfileImage] = useState(null)
    const [profileImagePreview, setProfileImagePreview] = useState(null)

    // Form state
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        contact: '',
        gender: 'male',
        dob: '',
        city: '',
        isExperienced: false,
        summary: '',
        experiences: [],
        educations: [],
        socialLinks: [],
    })

    // Validation errors
    const [errors, setErrors] = useState({})
    const [message, setMessage] = useState({ type: '', text: '' })
    const [messageCountdown, setMessageCountdown] = useState(0)

    // Modals
    const [showExperienceModal, setShowExperienceModal] = useState(false)
    const [showEducationModal, setShowEducationModal] = useState(false)
    const [showSocialModal, setShowSocialModal] = useState(false)
    const [editingIndex, setEditingIndex] = useState(null)

    // Modal form data
    const [experienceForm, setExperienceForm] = useState({
        title: '',
        company: '',
        startDate: { year: '', month: '', day: '' },
        endDate: { year: '', month: '', day: '' },
        description: '',
        isCurrent: false,
        city: '',
    })

    const [educationForm, setEducationForm] = useState({
        institution: '',
        degree: '',
        fieldOfStudy: '',
        startDate: { year: '', month: '', day: '' },
        endDate: { year: '', month: '', day: '' },
        description: '',
        isCurrent: false,
        city: '',
    })

    const [socialForm, setSocialForm] = useState({
        platform: '',
        url: '',
    })

    // Password update state
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    })
    const [showCurrentPassword, setShowCurrentPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const [updatingPassword, setUpdatingPassword] = useState(false)
    const [passwordErrors, setPasswordErrors] = useState({})

    useEffect(() => {
        if (!message.text) return;
        setMessageCountdown(5)
        const intervalId = setInterval(() => {
            setMessageCountdown((prev) => (prev > 0 ? prev - 1 : 0))
        }, 1000)
        const timeoutId = setTimeout(() => {
            setMessage({ type: '', text: '' })
        }, 5000)
        return () => {
            clearInterval(intervalId)
            clearTimeout(timeoutId)
        }
    }, [message.text])

    // Fetch student profile on mount
    useEffect(() => {
        const loadProfile = async () => {
            try {
                const data = await fetchStudentProfile({ BeURL , showAlert })
                if (data.success && data.student) {
                    const student = data.student
                    setStudentCode(student.studentCode)
                    setFormData({
                        fullName: student.fullName || '',
                        email: student.email || '',
                        contact: student.contact || '',
                        gender: student.gender || 'male',
                        dob: student.dob ? new Date(student.dob).toISOString().split('T')[0] : '',
                        city: student.city || '',
                        isExperienced: student.isExperienced || false,
                        summary: student.summary || '',
                        experiences: student.experiences || [],
                        educations: student.educations || [],
                        socialLinks: student.socialLinks || [],
                    })
                    if (student.profileImage) {
                        console.log("Profile image URL:", `${BeURL}/uploads/${student.profileImage}`)
                        setProfileImagePreview(`${BeURL}/uploads/${student.profileImage}`)
                    }
                }
                setLoading(false)
            } catch (err) {
                console.error('Error loading profile:', err)
                setLoading(false)
            }
        }

        if (BeURL) loadProfile()

    }, [BeURL, showAlert])

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target
        const newValue = type === 'checkbox' ? checked : value
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }))
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }))
        }
    }

    // Handle profile image
    const handleImageChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({
                    ...prev,
                    profileImage: 'Image must be less than 5MB'
                }))
                return
            }

            // Validate file type
            if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
                setErrors(prev => ({
                    ...prev,
                    profileImage: 'Only JPEG, PNG, and WebP images are allowed'
                }))
                return
            }

            setProfileImage(file)
            const reader = new FileReader()
            reader.onloadend = () => {
                setProfileImagePreview(reader.result)
            }
            reader.readAsDataURL(file)
            setErrors(prev => ({
                ...prev,
                profileImage: null
            }))
        }
    }

    // Validate basic info
    const validateBasicInfo = () => {
        const newErrors = {}

        const fullNameError = studentProfileValidation.fullName.validate(formData.fullName)
        if (fullNameError) newErrors.fullName = fullNameError

        const emailError = studentProfileValidation.email.validate(formData.email)
        if (emailError) newErrors.email = emailError

        const contactError = studentProfileValidation.contact.validate(formData.contact)
        if (contactError) newErrors.contact = contactError

        const genderError = studentProfileValidation.gender.validate(formData.gender)
        if (genderError) newErrors.gender = genderError

        const dobError = studentProfileValidation.dob.validate(formData.dob)
        if (dobError) newErrors.dob = dobError

        const cityError = studentProfileValidation.city.validate(formData.city)
        if (cityError) newErrors.city = cityError

        const summaryError = studentProfileValidation.summary.validate(formData.summary)
        if (summaryError) newErrors.summary = summaryError

        return newErrors
    }

    // Handle save profile
    const handleSaveProfile = async (e) => {
        e.preventDefault()
        setMessage({ type: '', text: '' })

        const newErrors = validateBasicInfo()
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            setMessage({
                type: 'error',
                text: 'Please fix the errors below'
            })
            window.scrollTo(0, 0)
            return
        }

        setSaving(true)
        try {
            const submissionData = {
                ...formData,
                contact: formData.contact.replace(/\D/g, '')
            }

            const formDataToSend = createProfileFormData(submissionData, profileImage)
            const result = await updateStudentProfile({ BeURL, formData: formDataToSend })

            if (result.success) {
                setMessage({
                    type: 'success',
                    text: 'Profile updated successfully!'
                })
                setProfileImage(null)
                setTimeout(() => {
                    setMessage({ type: '', text: '' })
                }, 3000)
            } else {
                setMessage({
                    type: 'error',
                    text: result.message || 'Failed to update profile'
                })
            }
        } catch (err) {
            console.error('Error saving profile:', err)
            setMessage({
                type: 'error',
                text: 'Failed to save profile. Please try again.'
            })
        } finally {
            setSaving(false)
        }
    }

    // Experience handlers
    const handleAddExperience = () => {
        setExperienceForm({
            title: '',
            company: '',
            startDate: { year: '', month: '', day: '' },
            endDate: { year: '', month: '', day: '' },
            description: '',
            isCurrent: false,
            city: '',
        })
        setEditingIndex(null)
        setShowExperienceModal(true)
    }

    const handleEditExperience = (index) => {
        setExperienceForm(formData.experiences[index])
        setEditingIndex(index)
        setShowExperienceModal(true)
    }

    const handleDeleteExperience = (index) => {
        setFormData(prev => ({
            ...prev,
            experiences: prev.experiences.filter((_, i) => i !== index)
        }))
    }

    const handleSaveExperience = () => {
        const errors = []
        if (!experienceForm.title.trim()) errors.push('Job title is required')
        if (!experienceForm.company.trim()) errors.push('Company is required')
        if (!experienceForm.startDate.year) errors.push('Start year is required')
        if (!experienceForm.isCurrent && !experienceForm.endDate.year) errors.push('End year is required (unless current)')
        if (!experienceForm.city.trim()) errors.push('City is required')

        if (errors.length > 0) {
            setMessage({ type: 'error', text: errors.join(' | ') })
            return
        }

        if (editingIndex !== null) {
            setFormData(prev => ({
                ...prev,
                experiences: prev.experiences.map((exp, i) => i === editingIndex ? experienceForm : exp)
            }))
        } else {
            setFormData(prev => ({
                ...prev,
                experiences: [...prev.experiences, experienceForm]
            }))
        }

        setShowExperienceModal(false)
        setMessage({ type: 'success', text: 'Experience added successfully' })
        setTimeout(() => setMessage({ type: '', text: '' }), 2000)
    }

    // Education handlers
    const handleAddEducation = () => {
        setEducationForm({
            institution: '',
            degree: '',
            fieldOfStudy: '',
            startDate: { year: '', month: '', day: '' },
            endDate: { year: '', month: '', day: '' },
            description: '',
            isCurrent: false,
            city: '',
        })
        setEditingIndex(null)
        setShowEducationModal(true)
    }

    const handleEditEducation = (index) => {
        setEducationForm(formData.educations[index])
        setEditingIndex(index)
        setShowEducationModal(true)
    }

    const handleDeleteEducation = (index) => {
        setFormData(prev => ({
            ...prev,
            educations: prev.educations.filter((_, i) => i !== index)
        }))
    }

    const handleSaveEducation = () => {
        const errors = []
        if (!educationForm.institution.trim()) errors.push('Institution is required')
        if (!educationForm.degree.trim()) errors.push('Degree is required')
        if (!educationForm.fieldOfStudy.trim()) errors.push('Field of study is required')
        if (!educationForm.startDate.year) errors.push('Start year is required')
        if (!educationForm.isCurrent && !educationForm.endDate.year) errors.push('End year is required (unless current)')
        if (!educationForm.city.trim()) errors.push('City is required')

        if (errors.length > 0) {
            setMessage({ type: 'error', text: errors.join(' | ') })
            return
        }

        if (editingIndex !== null) {
            setFormData(prev => ({
                ...prev,
                educations: prev.educations.map((edu, i) => i === editingIndex ? educationForm : edu)
            }))
        } else {
            setFormData(prev => ({
                ...prev,
                educations: [...prev.educations, educationForm]
            }))
        }

        setShowEducationModal(false)
        setMessage({ type: 'success', text: 'Education added successfully' })
        setTimeout(() => setMessage({ type: '', text: '' }), 2000)
    }

    // Social link handlers
    const handleAddSocialLink = () => {
        setSocialForm({ platform: '', url: '' })
        setEditingIndex(null)
        setShowSocialModal(true)
    }

    const handleEditSocialLink = (index) => {
        setSocialForm(formData.socialLinks[index])
        setEditingIndex(index)
        setShowSocialModal(true)
    }

    const handleDeleteSocialLink = (index) => {
        setFormData(prev => ({
            ...prev,
            socialLinks: prev.socialLinks.filter((_, i) => i !== index)
        }))
    }

    const handleSaveSocialLink = () => {
        const errors = []
        if (!socialForm.platform.trim()) errors.push('Platform is required')
        if (!socialForm.url.trim()) errors.push('URL is required')
        if (socialForm.url) {
            try {
                new URL(socialForm.url)
            } catch {
                errors.push('Please enter a valid URL')
            }
        }

        if (errors.length > 0) {
            setMessage({ type: 'error', text: errors.join(' | ') })
            return
        }

        if (editingIndex !== null) {
            setFormData(prev => ({
                ...prev,
                socialLinks: prev.socialLinks.map((link, i) => i === editingIndex ? socialForm : link)
            }))
        } else {
            setFormData(prev => ({
                ...prev,
                socialLinks: [...prev.socialLinks, socialForm]
            }))
        }

        setShowSocialModal(false)
        setMessage({ type: 'success', text: 'Social link added successfully' })
        setTimeout(() => setMessage({ type: '', text: '' }), 2000)
    }

    // Password update handlers
    const handlePasswordInputChange = (e) => {
        const { name, value } = e.target
        setPasswordForm(prev => ({
            ...prev,
            [name]: value
        }))
        // Clear error for this field
        if (passwordErrors[name]) {
            setPasswordErrors(prev => ({
                ...prev,
                [name]: null
            }))
        }
    }

    const validatePasswordUpdate = () => {
        const errors = {}

        // Current password validation
        if (!passwordForm.currentPassword) {
            errors.currentPassword = 'Current password is required'
        } else if (passwordForm.currentPassword.length < 3) {
            errors.currentPassword = 'Current password seems too short'
        }

        // New password validation - Strong requirements
        if (!passwordForm.newPassword) {
            errors.newPassword = 'New password is required'
        } else {
            const passwordValidations = []

            // Minimum length
            if (passwordForm.newPassword.length < 8) {
                passwordValidations.push('at least 8 characters')
            }

            // Maximum length check
            if (passwordForm.newPassword.length > 128) {
                passwordValidations.push('maximum 128 characters')
            }

            // Uppercase letter
            if (!/[A-Z]/.test(passwordForm.newPassword)) {
                passwordValidations.push('at least one uppercase letter (A-Z)')
            }

            // Lowercase letter
            if (!/[a-z]/.test(passwordForm.newPassword)) {
                passwordValidations.push('at least one lowercase letter (a-z)')
            }

            // Number
            if (!/\d/.test(passwordForm.newPassword)) {
                passwordValidations.push('at least one number (0-9)')
            }

            // Special character
            if (!/[\W_]/.test(passwordForm.newPassword)) {
                passwordValidations.push('at least one special character (!@#$%^&*)')
            }

            // Check if new password is same as current password
            if (passwordForm.currentPassword && passwordForm.newPassword === passwordForm.currentPassword) {
                passwordValidations.push('new password must be different from current password')
            }

            if (passwordValidations.length > 0) {
                errors.newPassword = `Password must contain: ${passwordValidations.join(', ')}`
            }
        }

        // Confirm password validation
        if (!passwordForm.confirmPassword) {
            errors.confirmPassword = 'Please confirm your new password'
        } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match'
        }

        return errors
    }

    const handleUpdatePassword = async (e) => {
        e.preventDefault()
        setMessage({ type: '', text: '' })

        const errors = validatePasswordUpdate()
        if (Object.keys(errors).length > 0) {
            setPasswordErrors(errors)
            setMessage({
                type: 'error',
                text: 'Please fix the password errors'
            })
            return
        }

        setUpdatingPassword(true)
        try {
            const res = await fetch(`${BeURL}/update-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    currentPassword: passwordForm.currentPassword,
                    password: passwordForm.newPassword,
                }),
            })

            const result = await res.json()

            if (result.success) {
                setMessage({
                    type: 'success',
                    text: 'Password updated successfully!'
                })
                // Reset form
                setPasswordForm({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                })
                setPasswordErrors({})
            } else {
                setMessage({
                    type: 'error',
                    text: result.message || 'Failed to update password'
                })
            }
        } catch (err) {
            console.error('Error updating password:', err)
            setMessage({
                type: 'error',
                text: 'Failed to update password. Please try again.'
            })
        } finally {
            setUpdatingPassword(false)
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin">
                    <div className="w-16 h-16 border-4 border-gray-200 border-t-secondary-600 rounded-full"></div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 py-6 sm:py-8 lg:py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8 animate-fade-in">
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 flex items-center gap-3 mb-2">
                        <FiUser className="text-secondary-600" size={32} />
                        My Profile
                    </h1>
                    {studentCode && (
                        <p className="text-sm text-gray-600">
                            Student Code: <span className="font-semibold text-primary-600">{studentCode}</span>
                        </p>
                    )}
                </div>

                {/* Message Alert */}
                {message.text && (
                    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-[calc(100%-2rem)] max-w-3xl">
                        <div className={`p-4 rounded-lg flex items-center gap-3 shadow-lg animate-slide-in-top ${message.type === 'success'
                            ? 'bg-green-50 border border-green-200 text-green-800'
                            : 'bg-red-50 border border-red-200 text-red-800'
                            }`}>
                            {message.type === 'success' ? (
                                <FiCheckCircle size={20} className="flex-shrink-0" />
                            ) : (
                                <FiAlertCircle size={20} className="flex-shrink-0" />
                            )}
                            <span className="text-sm font-medium">
                                {message.text}
                                {messageCountdown > 0 && (
                                    <span className="ml-2 text-xs opacity-70">({messageCountdown}s)</span>
                                )}
                            </span>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSaveProfile} className="space-y-8">
                    {/* Profile Image Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 hover:shadow-md transition-shadow duration-200">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                            <FiCamera className="text-secondary-600" />
                            Profile Photo
                        </h2>

                        <div className="flex flex-col sm:flex-row gap-6 items-start">
                            {/* Preview */}
                            <div className="flex-shrink-0">
                                <div className="w-32 h-32 rounded-xl bg-gradient-to-br from-primary-100 to-secondary-100 flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300 shadow-md">
                                    {profileImagePreview ? (
                                        <img src={profileImagePreview} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                        <FiUser size={48} className="text-gray-400" />
                                    )}
                                </div>
                            </div>

                            {/* Upload */}
                            <div className="flex-1">
                                <label className="block">
                                    <div className="flex items-center justify-center px-6 py-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-secondary-600 hover:bg-secondary-50 transition-all duration-200 group">
                                        <div className="text-center">
                                            <FiUpload className="mx-auto h-8 w-8 text-gray-400 group-hover:text-secondary-600 transition-colors mb-2" />
                                            <p className="text-sm font-medium text-gray-900">Click to upload or drag and drop</p>
                                            <p className="text-xs text-gray-500 mt-1">PNG, JPG, WebP up to 5MB</p>
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp"
                                            onChange={handleImageChange}
                                            className="hidden"
                                        />
                                    </div>
                                </label>
                                {errors.profileImage && (
                                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {errors.profileImage}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Basic Information */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 hover:shadow-md transition-shadow duration-200">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                            <FiUser className="text-secondary-600" />
                            Basic Information
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Full Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition ${errors.fullName ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                                        }`}
                                    placeholder="John Doe"
                                />
                                {errors.fullName && (
                                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {errors.fullName}
                                    </p>
                                )}
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                                        }`}
                                    placeholder="john@example.com"
                                />
                                {errors.email && (
                                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {errors.email}
                                    </p>
                                )}
                            </div>

                            {/* Contact */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Contact Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    name="contact"
                                    value={formData.contact}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition ${errors.contact ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                                        }`}
                                    placeholder="9876543210"
                                    maxLength="10"
                                />
                                {errors.contact && (
                                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {errors.contact}
                                    </p>
                                )}
                            </div>

                            {/* Gender */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Gender <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="gender"
                                    value={formData.gender}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition ${errors.gender ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                                        }`}
                                >
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                                {errors.gender && (
                                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {errors.gender}
                                    </p>
                                )}
                            </div>

                            {/* Date of Birth */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Date of Birth <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    name="dob"
                                    value={formData.dob}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition ${errors.dob ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                                        }`}
                                />
                                {errors.dob && (
                                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {errors.dob}
                                    </p>
                                )}
                            </div>

                            {/* City */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    City <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition ${errors.city ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                                        }`}
                                    placeholder="New York"
                                />
                                {errors.city && (
                                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {errors.city}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Summary */}
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Professional Summary
                            </label>
                            <textarea
                                name="summary"
                                value={formData.summary}
                                onChange={handleInputChange}
                                rows="4"
                                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition resize-none ${errors.summary ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                                    }`}
                                placeholder="Brief summary about yourself (up to 1000 characters)"
                            />
                            <div className="flex justify-between items-center mt-2">
                                {errors.summary && (
                                    <p className="text-red-600 text-sm flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {errors.summary}
                                    </p>
                                )}
                                <span className="text-xs text-gray-500">{formData.summary.length}/1000</span>
                            </div>
                        </div>

                        {/* Is Experienced */}
                        <div className="mt-6">
                            <label className="flex items-center gap-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    name="isExperienced"
                                    checked={formData.isExperienced}
                                    onChange={handleInputChange}
                                    className="w-5 h-5 accent-secondary-600 rounded cursor-pointer"
                                />
                                <span className="text-sm font-medium text-gray-700 group-hover:text-primary-600 transition-colors">
                                    I have professional experience
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Experiences Section */}
                     {
                        formData.isExperienced && 
                        (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 hover:shadow-md transition-shadow duration-200">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                        <FiBriefcase className="text-secondary-600" />
                                        Professional Experience
                                    </h2>
                                    <button
                                        type="button"
                                        onClick={handleAddExperience}
                                        className="flex items-center gap-2 px-4 py-2 bg-secondary-600 hover:bg-secondary-700 text-white rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
                                    >
                                        <FiPlus size={18} />
                                        Add Experience
                                    </button>
                                </div>

                                {formData.experiences.length > 0 ? (
                                    <div className="space-y-4">
                                        {formData.experiences.map((exp, idx) => (
                                            <div
                                                key={idx}
                                                className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-secondary-600 hover:bg-secondary-50 transition-all duration-200"
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <h3 className="font-semibold text-gray-900">{exp.title}</h3>
                                                        <p className="text-sm text-gray-600">{exp.company}</p>
                                                        <p className="text-xs text-gray-500 mt-2">
                                                            {exp.startDate.year} - {exp.isCurrent ? 'Present' : exp.endDate.year} • {exp.city}
                                                        </p>
                                                        {exp.description && (
                                                            <p className="text-sm text-gray-700 mt-2">{exp.description}</p>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-2 ml-4">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleEditExperience(idx)}
                                                            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                                            title="Edit"
                                                        >
                                                            <FiEdit2 size={18} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteExperience(idx)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Delete"
                                                        >
                                                            <FiTrash2 size={18} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-sm italic">No experiences added yet</p>
                                )}
                            </div>
                        )
                     }

                    {/* Education Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 hover:shadow-md transition-shadow duration-200">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                <FiBook className="text-secondary-600" />
                                Education
                            </h2>
                            <button
                                type="button"
                                onClick={handleAddEducation}
                                className="flex items-center gap-2 px-4 py-2 bg-secondary-600 hover:bg-secondary-700 text-white rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
                            >
                                <FiPlus size={18} />
                                Add Education
                            </button>
                        </div>

                        {formData.educations.length > 0 ? (
                            <div className="space-y-4">
                                {formData.educations.map((edu, idx) => (
                                    <div
                                        key={idx}
                                        className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-secondary-600 hover:bg-secondary-50 transition-all duration-200"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-900">{edu.degree} in {edu.fieldOfStudy}</h3>
                                                <p className="text-sm text-gray-600">{edu.institution}</p>
                                                <p className="text-xs text-gray-500 mt-2">
                                                    {edu.startDate.year} - {edu.isCurrent ? 'Present' : edu.endDate.year} • {edu.city}
                                                </p>
                                                {edu.description && (
                                                    <p className="text-sm text-gray-700 mt-2">{edu.description}</p>
                                                )}
                                            </div>
                                            <div className="flex gap-2 ml-4">
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditEducation(idx)}
                                                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <FiEdit2 size={18} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteEducation(idx)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete"
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-sm italic">No education added yet</p>
                        )}
                    </div>

                    {/* Social Links Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 hover:shadow-md transition-shadow duration-200">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                                <FiLink className="text-secondary-600" />
                                Social Links
                            </h2>
                            <button
                                type="button"
                                onClick={handleAddSocialLink}
                                className="flex items-center gap-2 px-4 py-2 bg-secondary-600 hover:bg-secondary-700 text-white rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
                            >
                                <FiPlus size={18} />
                                Add Link
                            </button>
                        </div>

                        {formData.socialLinks.length > 0 ? (
                            <div className="space-y-3">
                                {formData.socialLinks.map((link, idx) => (
                                    <div
                                        key={idx}
                                        className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-secondary-600 hover:bg-secondary-50 transition-all duration-200 flex justify-between items-center"
                                    >
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 text-sm">{link.platform}</p>
                                            <a
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-secondary-600 hover:text-secondary-700 text-xs break-all"
                                            >
                                                {link.url}
                                            </a>
                                        </div>
                                        <div className="flex gap-2 ml-4">
                                            <button
                                                type="button"
                                                onClick={() => handleEditSocialLink(idx)}
                                                className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                                title="Edit"
                                            >
                                                <FiEdit2 size={18} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteSocialLink(idx)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete"
                                            >
                                                <FiTrash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-sm italic">No social links added yet</p>
                        )}
                    </div>

                    {/* Profile Submit Button */}
                    <div className="flex justify-center gap-4">
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-gray-400 disabled:to-gray-400 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                        >
                            {saving ? (
                                <>
                                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                                    Saving Profile...
                                </>
                            ) : (
                                <>
                                    <FiSave size={20} />
                                    Save Profile
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Password Update Section - Separate Block */}
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg shadow-lg border-2 border-orange-200 p-6 sm:p-8 hover:shadow-xl transition-shadow duration-200 mt-8">
                    <div className="bg-white rounded-lg p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <FiLock className="text-red-600" />
                            Security - Change Password
                        </h2>
                        <p className="text-sm text-gray-600 mb-6">
                            Ensure your password is strong and unique. Never share your password with anyone.
                        </p>

                        <form onSubmit={handleUpdatePassword} className="space-y-5">
                            {/* Current Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Current Password <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type={showCurrentPassword ? 'text' : 'password'}
                                        name="currentPassword"
                                        value={passwordForm.currentPassword}
                                        onChange={handlePasswordInputChange}
                                        className={`w-full px-4 py-2.5 pr-12 border rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition ${passwordErrors.currentPassword ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                                            }`}
                                        placeholder="Enter current password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    >
                                        {showCurrentPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                                    </button>
                                </div>
                                {passwordErrors.currentPassword && (
                                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {passwordErrors.currentPassword}
                                    </p>
                                )}
                            </div>

                            {/* New Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    New Password <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? 'text' : 'password'}
                                        name="newPassword"
                                        value={passwordForm.newPassword}
                                        onChange={handlePasswordInputChange}
                                        className={`w-full px-4 py-2.5 pr-12 border rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition ${passwordErrors.newPassword ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                                            }`}
                                        placeholder="Enter new password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    >
                                        {showNewPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                                    </button>
                                </div>
                                {passwordErrors.newPassword && (
                                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {passwordErrors.newPassword}
                                    </p>
                                )}
                                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-xs font-semibold text-blue-800 mb-2">Strong Password Requirements:</p>
                                    <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                                        <li>At least 8 characters (recommended: 12+)</li>
                                        <li>At least one uppercase letter (A-Z)</li>
                                        <li>At least one lowercase letter (a-z)</li>
                                        <li>At least one number (0-9)</li>
                                        <li>At least one special character (!@#$%^&*)</li>
                                    </ul>
                                </div>
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Confirm New Password <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        name="confirmPassword"
                                        value={passwordForm.confirmPassword}
                                        onChange={handlePasswordInputChange}
                                        className={`w-full px-4 py-2.5 pr-12 border rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition ${passwordErrors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                                            }`}
                                        placeholder="Confirm new password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    >
                                        {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                                    </button>
                                </div>
                                {passwordErrors.confirmPassword && (
                                    <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                        <FiAlertCircle size={16} />
                                        {passwordErrors.confirmPassword}
                                    </p>
                                )}
                            </div>

                            <div className="pt-2 border-t border-gray-200">
                                <button
                                    type="submit"
                                    disabled={updatingPassword}
                                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-400 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 disabled:cursor-not-allowed shadow-md"
                                >
                                    {updatingPassword ? (
                                        <>
                                            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                                            Updating Password...
                                        </>
                                    ) : (
                                        <>
                                            <FiLock size={20} />
                                            Update Password
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Experience Modal */}
            {showExperienceModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-in-bottom">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-900">
                                {editingIndex !== null ? 'Edit Experience' : 'Add Experience'}
                            </h3>
                            <button
                                onClick={() => setShowExperienceModal(false)}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <FiX size={24} />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    placeholder="Job Title"
                                    value={experienceForm.title}
                                    onChange={(e) => setExperienceForm(prev => ({ ...prev, title: e.target.value }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                                <input
                                    type="text"
                                    placeholder="Company Name"
                                    value={experienceForm.company}
                                    onChange={(e) => setExperienceForm(prev => ({ ...prev, company: e.target.value }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    placeholder="City"
                                    value={experienceForm.city}
                                    onChange={(e) => setExperienceForm(prev => ({ ...prev, city: e.target.value }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <input
                                    type="number"
                                    placeholder="Start Year"
                                    value={experienceForm.startDate.year}
                                    onChange={(e) => setExperienceForm(prev => ({
                                        ...prev,
                                        startDate: { ...prev.startDate, year: e.target.value }
                                    }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                                <input
                                    type="number"
                                    placeholder="Month"
                                    min="1"
                                    max="12"
                                    value={experienceForm.startDate.month}
                                    onChange={(e) => setExperienceForm(prev => ({
                                        ...prev,
                                        startDate: { ...prev.startDate, month: e.target.value }
                                    }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                                <input
                                    type="number"
                                    placeholder="Day"
                                    min="1"
                                    max="31"
                                    value={experienceForm.startDate.day}
                                    onChange={(e) => setExperienceForm(prev => ({
                                        ...prev,
                                        startDate: { ...prev.startDate, day: e.target.value }
                                    }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="experienceCurrent"
                                    checked={experienceForm.isCurrent}
                                    onChange={(e) => setExperienceForm(prev => ({ ...prev, isCurrent: e.target.checked }))}
                                    className="w-5 h-5 accent-secondary-600 rounded cursor-pointer"
                                />
                                <label htmlFor="experienceCurrent" className="text-sm font-medium text-gray-700 cursor-pointer">
                                    I currently work here
                                </label>
                            </div>

                            {!experienceForm.isCurrent && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <input
                                        type="number"
                                        placeholder="End Year"
                                        value={experienceForm.endDate.year}
                                        onChange={(e) => setExperienceForm(prev => ({
                                            ...prev,
                                            endDate: { ...prev.endDate, year: e.target.value }
                                        }))}
                                        className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                    />
                                    <input
                                        type="number"
                                        placeholder="Month"
                                        min="1"
                                        max="12"
                                        value={experienceForm.endDate.month}
                                        onChange={(e) => setExperienceForm(prev => ({
                                            ...prev,
                                            endDate: { ...prev.endDate, month: e.target.value }
                                        }))}
                                        className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                    />
                                    <input
                                        type="number"
                                        placeholder="Day"
                                        min="1"
                                        max="31"
                                        value={experienceForm.endDate.day}
                                        onChange={(e) => setExperienceForm(prev => ({
                                            ...prev,
                                            endDate: { ...prev.endDate, day: e.target.value }
                                        }))}
                                        className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                    />
                                </div>
                            )}

                            <textarea
                                placeholder="Description (optional)"
                                value={experienceForm.description}
                                onChange={(e) => setExperienceForm(prev => ({ ...prev, description: e.target.value }))}
                                rows="3"
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition resize-none"
                            />
                        </div>

                        <div className="flex gap-3 p-6 border-t border-gray-200 justify-end">
                            <button
                                type="button"
                                onClick={() => setShowExperienceModal(false)}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveExperience}
                                className="px-6 py-2 bg-secondary-600 hover:bg-secondary-700 text-white rounded-lg transition-colors font-medium"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Education Modal */}
            {showEducationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-in-bottom">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-900">
                                {editingIndex !== null ? 'Edit Education' : 'Add Education'}
                            </h3>
                            <button
                                onClick={() => setShowEducationModal(false)}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <FiX size={24} />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    placeholder="Institution/School Name"
                                    value={educationForm.institution}
                                    onChange={(e) => setEducationForm(prev => ({ ...prev, institution: e.target.value }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                                <input
                                    type="text"
                                    placeholder="Degree"
                                    value={educationForm.degree}
                                    onChange={(e) => setEducationForm(prev => ({ ...prev, degree: e.target.value }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    placeholder="Field of Study"
                                    value={educationForm.fieldOfStudy}
                                    onChange={(e) => setEducationForm(prev => ({ ...prev, fieldOfStudy: e.target.value }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                                <input
                                    type="text"
                                    placeholder="City"
                                    value={educationForm.city}
                                    onChange={(e) => setEducationForm(prev => ({ ...prev, city: e.target.value }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <input
                                    type="number"
                                    placeholder="Start Year"
                                    value={educationForm.startDate.year}
                                    onChange={(e) => setEducationForm(prev => ({
                                        ...prev,
                                        startDate: { ...prev.startDate, year: e.target.value }
                                    }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                                <input
                                    type="number"
                                    placeholder="Month"
                                    min="1"
                                    max="12"
                                    value={educationForm.startDate.month}
                                    onChange={(e) => setEducationForm(prev => ({
                                        ...prev,
                                        startDate: { ...prev.startDate, month: e.target.value }
                                    }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                                <input
                                    type="number"
                                    placeholder="Day"
                                    min="1"
                                    max="31"
                                    value={educationForm.startDate.day}
                                    onChange={(e) => setEducationForm(prev => ({
                                        ...prev,
                                        startDate: { ...prev.startDate, day: e.target.value }
                                    }))}
                                    className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="educationCurrent"
                                    checked={educationForm.isCurrent}
                                    onChange={(e) => setEducationForm(prev => ({ ...prev, isCurrent: e.target.checked }))}
                                    className="w-5 h-5 accent-secondary-600 rounded cursor-pointer"
                                />
                                <label htmlFor="educationCurrent" className="text-sm font-medium text-gray-700 cursor-pointer">
                                    I currently study here
                                </label>
                            </div>

                            {!educationForm.isCurrent && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <input
                                        type="number"
                                        placeholder="End Year"
                                        value={educationForm.endDate.year}
                                        onChange={(e) => setEducationForm(prev => ({
                                            ...prev,
                                            endDate: { ...prev.endDate, year: e.target.value }
                                        }))}
                                        className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                    />
                                    <input
                                        type="number"
                                        placeholder="Month"
                                        min="1"
                                        max="12"
                                        value={educationForm.endDate.month}
                                        onChange={(e) => setEducationForm(prev => ({
                                            ...prev,
                                            endDate: { ...prev.endDate, month: e.target.value }
                                        }))}
                                        className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                    />
                                    <input
                                        type="number"
                                        placeholder="Day"
                                        min="1"
                                        max="31"
                                        value={educationForm.endDate.day}
                                        onChange={(e) => setEducationForm(prev => ({
                                            ...prev,
                                            endDate: { ...prev.endDate, day: e.target.value }
                                        }))}
                                        className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                                    />
                                </div>
                            )}

                            <textarea
                                placeholder="Description (optional)"
                                value={educationForm.description}
                                onChange={(e) => setEducationForm(prev => ({ ...prev, description: e.target.value }))}
                                rows="3"
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition resize-none"
                            />
                        </div>

                        <div className="flex gap-3 p-6 border-t border-gray-200 justify-end">
                            <button
                                type="button"
                                onClick={() => setShowEducationModal(false)}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveEducation}
                                className="px-6 py-2 bg-secondary-600 hover:bg-secondary-700 text-white rounded-lg transition-colors font-medium"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Social Link Modal */}
            {showSocialModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-2xl max-w-xl w-full animate-slide-in-bottom">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-xl font-semibold text-gray-900">
                                {editingIndex !== null ? 'Edit Social Link' : 'Add Social Link'}
                            </h3>
                            <button
                                onClick={() => setShowSocialModal(false)}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <FiX size={24} />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <input
                                type="text"
                                placeholder="Platform (e.g., LinkedIn, GitHub, Twitter)"
                                value={socialForm.platform}
                                onChange={(e) => setSocialForm(prev => ({ ...prev, platform: e.target.value }))}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                            />
                            <input
                                type="url"
                                placeholder="URL"
                                value={socialForm.url}
                                onChange={(e) => setSocialForm(prev => ({ ...prev, url: e.target.value }))}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-600 focus:border-transparent outline-none transition"
                            />
                        </div>

                        <div className="flex gap-3 p-6 border-t border-gray-200 justify-end">
                            <button
                                type="button"
                                onClick={() => setShowSocialModal(false)}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveSocialLink}
                                className="px-6 py-2 bg-secondary-600 hover:bg-secondary-700 text-white rounded-lg transition-colors font-medium"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default StudentProfile
